import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays } from 'date-fns';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { DateInput } from '~/components/date-input';
import { Pill } from '~/components/pill';
import { EditorSection, Field } from '~/components/promotion/editor-section';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

export function TimeWindowSection() {
	const { t } = useTranslation();
	const { control } = useFormContext<PromotionFormValues>();
	const from = useWatch({ control, name: 'activeFrom' });
	const to = useWatch({ control, name: 'activeTo' });

	const durationDays = from && to ? differenceInCalendarDays(to, from) : null;
	const now = new Date();
	const isScheduled = Boolean(from && now < from);
	const daysUntil = from ? differenceInCalendarDays(from, now) : 0;

	return (
		<EditorSection
			title={t('promotion.conditions.timeWindow.title')}
			subtitle={t('promotion.conditions.timeWindow.subtitle')}
			icon={Calendar}
			right={
				durationDays != null ? (
					<Pill variant="primary" icon={Clock}>
						{t('promotion.conditions.timeWindow.durationDays', { count: durationDays })}
					</Pill>
				) : undefined
			}>
			<div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
				<Field label={t('promotion.conditions.timeWindow.from')}>
					<Controller
						control={control}
						name="activeFrom"
						render={({ field }) => <DateInput value={field.value} onChange={field.onChange} />}
					/>
				</Field>
				<ArrowRight size={16} className="mb-2.5 text-foreground-subtle" />
				<Field label={t('promotion.conditions.timeWindow.to')}>
					<Controller
						control={control}
						name="activeTo"
						render={({ field }) => <DateInput value={field.value} onChange={field.onChange} />}
					/>
				</Field>
			</div>
			<p className="mt-2 text-xs text-foreground-muted">{t('promotion.conditions.timeWindow.help')}</p>
			{isScheduled && (
				<p className="mt-2 text-xs text-info-fg">{t('promotion.conditions.timeWindow.scheduledNote', { count: daysUntil })}</p>
			)}
		</EditorSection>
	);
}
